// Authenticated API client. Always attaches Bearer token from authService.
// On 401, attempts a single refresh-and-retry before returning the error.

import { getAccessToken, refreshAccessToken } from '@/lib/authService';

const API_BASE =
  import.meta.env.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`API error ${status}`);
  }
}

async function withAuth(init: RequestInit): Promise<RequestInit> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  return { ...init, headers };
}

/** The raw authenticated round-trip: auth header, one refresh-and-retry on
 *  401, ApiError on a non-2xx (with the JSON body when the server sent one).
 *  Callers that want JSON use `request`; callers that want BYTES use this. */
async function requestRaw(path: string, init: RequestInit = {}): Promise<Response> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  let res = await fetch(url, await withAuth(init));
  if (res.status === 401 && (await refreshAccessToken())) {
    res = await fetch(url, await withAuth(init));
  }
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* empty or non-JSON body */ }
    throw new ApiError(res.status, body);
  }
  return res;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await requestRaw(path, init);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) => {
    const init: RequestInit = { method: 'POST' };
    if (body !== undefined) init.body = JSON.stringify(body);
    return request<T>(path, init);
  },
  patch: <T>(path: string, body?: unknown) => {
    const init: RequestInit = { method: 'PATCH' };
    if (body !== undefined) init.body = JSON.stringify(body);
    return request<T>(path, init);
  },
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Authenticated fetch of raw bytes — for streams the browser renders itself
   *  (an inline PDF), where a download link would be the wrong affordance.
   *  `mimeType` re-stamps the Blob so an <iframe> renders it even if a proxy
   *  flattened the Content-Type on the way. */
  blob: async (path: string, mimeType?: string): Promise<Blob> => {
    const res = await requestRaw(path, { method: 'GET' });
    const bytes = await res.arrayBuffer();
    return new Blob([bytes], {
      type: mimeType ?? res.headers.get('Content-Type') ?? 'application/octet-stream',
    });
  },
};
